import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Filter,
  Eye,
  List,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { mockAppointments } from '@mocks/appointments';
import type { MockAppointment } from '@mocks/appointments';
import { mockApplicants } from '@mocks/applicants';
import { mockCenters } from '@mocks/bookings';

export const BookingManagement: React.FC = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<MockAppointment[]>(mockAppointments);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'SCHEDULED' | 'COMPLETED' | 'CANCELLED'>('all');
  const [filterCenter, setFilterCenter] = useState<number | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState<MockAppointment | null>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const itemsPerPage = 5;

  // Filter appointments
  const filteredAppointments = appointments.filter((apt) => {
    const applicant = mockApplicants.find((a) => a.id === apt.applicant_id);
    const applicantName = applicant
      ? `${applicant.first_name} ${applicant.last_name}`.toLowerCase()
      : '';

    const matchesSearch =
      applicantName.includes(searchQuery.toLowerCase()) ||
      apt.booking_reference.toLowerCase().includes(searchQuery.toLowerCase()) ||
      apt.test_type.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = filterStatus === 'all' || apt.status === filterStatus;
    const matchesCenter = filterCenter === 'all' || apt.center_id === filterCenter;

    return matchesSearch && matchesStatus && matchesCenter;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SCHEDULED':
        return <Badge variant="info" icon={<Clock className="h-3 w-3" />}>Scheduled</Badge>;
      case 'COMPLETED':
        return <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />}>Completed</Badge>;
      case 'CANCELLED':
        return <Badge variant="error" icon={<XCircle className="h-3 w-3" />}>Cancelled</Badge>;
      case 'NO_SHOW':
        return <Badge variant="error" icon={<AlertCircle className="h-3 w-3" />}>No Show</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const handleCancelAppointment = (appointmentId: number) => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      setAppointments(
        appointments.map((apt) =>
          apt.id === appointmentId ? { ...apt, status: 'CANCELLED' as const } : apt
        )
      );
      toast.success('Appointment cancelled successfully');
      console.log('❌ Mock: Cancelled appointment', appointmentId);
      setShowAppointmentModal(false);
      setSelectedAppointment(null);
    }
  };

  const handleMarkCompleted = (appointmentId: number) => {
    setAppointments(
      appointments.map((apt) =>
        apt.id === appointmentId ? { ...apt, status: 'COMPLETED' as const } : apt
      )
    );
    toast.success('Appointment marked as completed');
    console.log('✅ Mock: Completed appointment', appointmentId);
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
  };

  const handleMarkNoShow = (appointmentId: number) => {
    setAppointments(
      appointments.map((apt) =>
        apt.id === appointmentId ? { ...apt, status: 'NO_SHOW' as const } : apt
      )
    );
    toast.success('Appointment marked as no-show');
    console.log('⚠️ Mock: No-show appointment', appointmentId);
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
  };

  // Stats
  const stats = {
    total: appointments.length,
    scheduled: appointments.filter((a) => a.status === 'SCHEDULED').length,
    completed: appointments.filter((a) => a.status === 'COMPLETED').length,
    cancelled: appointments.filter((a) => a.status === 'CANCELLED').length,
  };

  // Pagination
  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAppointments = filteredAppointments.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, filterCenter]);

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getAppointmentsForDate = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    return filteredAppointments.filter((apt) => apt.appointment_date === dateString);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ncaa-dark">Booking Management</h1>
            <p className="text-ncaa-medium mt-1">Oversee all testing appointments and schedules</p>
          </div>
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-ncaa-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="h-4 w-4" />
              <span className="font-medium">List View</span>
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-white text-ncaa-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CalendarDays className="h-4 w-4" />
              <span className="font-medium">Calendar View</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Bookings</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Scheduled</p>
              <p className="text-2xl font-bold text-gray-900">{stats.scheduled}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Cancelled</p>
              <p className="text-2xl font-bold text-gray-900">{stats.cancelled}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by applicant, reference, or test type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-gray-400" />
            <select
              value={filterCenter}
              onChange={(e) =>
                setFilterCenter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))
              }
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
            >
              <option value="all">All Centers</option>
              {mockCenters.map((center) => (
                <option key={center.id} value={center.id}>
                  {center.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Card>
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigateMonth('prev')}
                icon={<ChevronLeft className="h-4 w-4" />}
              />
              <h2 className="text-xl font-bold text-gray-900">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigateMonth('next')}
                icon={<ChevronRight className="h-4 w-4" />}
              />
            </div>
            <Button size="sm" onClick={goToToday}>
              Today
            </Button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden">
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div
                key={day}
                className="bg-gray-50 py-3 text-center text-sm font-semibold text-gray-700"
              >
                {day}
              </div>
            ))}

            {/* Calendar Days */}
            {(() => {
              const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
              const days = [];

              // Empty cells for days before month starts
              for (let i = 0; i < startingDayOfWeek; i++) {
                days.push(
                  <div key={`empty-${i}`} className="bg-gray-50 min-h-[120px]" />
                );
              }

              // Days of the month
              for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(year, month, day);
                const dayAppointments = getAppointmentsForDate(date);
                const today = isToday(date);

                days.push(
                  <div
                    key={day}
                    className={`bg-white min-h-[120px] p-2 relative ${
                      today ? 'ring-2 ring-ncaa-primary' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`text-sm font-semibold ${
                          today
                            ? 'bg-ncaa-primary text-white w-6 h-6 rounded-full flex items-center justify-center'
                            : 'text-gray-900'
                        }`}
                      >
                        {day}
                      </span>
                      {dayAppointments.length > 0 && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                          {dayAppointments.length}
                        </span>
                      )}
                    </div>

                    {/* Appointments for this day */}
                    <div className="space-y-1">
                      {dayAppointments.slice(0, 3).map((apt) => {
                        const applicant = mockApplicants.find((a) => a.id === apt.applicant_id);
                        return (
                          <div
                            key={apt.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAppointment(apt);
                              setShowAppointmentModal(true);
                            }}
                            className={`text-xs p-1.5 rounded cursor-pointer hover:shadow-md transition-all ${
                              apt.status === 'SCHEDULED'
                                ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                : apt.status === 'COMPLETED'
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : apt.status === 'CANCELLED'
                                ? 'bg-red-100 text-red-800 hover:bg-red-200'
                                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                            }`}
                          >
                            <div className="font-medium truncate">{apt.time_slot}</div>
                            <div className="truncate opacity-90">
                              {applicant?.first_name} {applicant?.last_name}
                            </div>
                            <div className="truncate text-[10px] opacity-75">{apt.test_type}</div>
                          </div>
                        );
                      })}
                      {dayAppointments.length > 3 && (
                        <div className="text-xs text-gray-600 text-center py-1">
                          +{dayAppointments.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              return days;
            })()}
          </div>

          {/* Calendar Legend */}
          <div className="flex items-center gap-6 mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-100 rounded"></div>
              <span className="text-sm text-gray-600">Scheduled</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-100 rounded"></div>
              <span className="text-sm text-gray-600">Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-100 rounded"></div>
              <span className="text-sm text-gray-600">Cancelled</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 border-2 border-ncaa-primary rounded"></div>
              <span className="text-sm text-gray-600">Today</span>
            </div>
          </div>
        </Card>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-4">
        {/* Results Info */}
        {filteredAppointments.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <p>
              Showing {startIndex + 1} to {Math.min(endIndex, filteredAppointments.length)} of{' '}
              {filteredAppointments.length} appointments
            </p>
          </div>
        )}

        {filteredAppointments.length === 0 ? (
          <Card>
            <div className="text-center py-16">
              <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Appointments Found</h3>
              <p className="text-gray-600">Try adjusting your search or filters</p>
            </div>
          </Card>
        ) : (
          paginatedAppointments.map((apt) => {
            const applicant = mockApplicants.find((a) => a.id === apt.applicant_id);
            const center = mockCenters.find((c) => c.id === apt.center_id);

            return (
              <Card key={apt.id}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <Calendar className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-gray-900">{apt.test_type}</h3>
                          {getStatusBadge(apt.status)}
                        </div>
                        <p className="text-sm text-gray-600">Ref: {apt.booking_reference}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Applicant</p>
                          <p
                            className="text-sm font-medium text-blue-600 cursor-pointer hover:underline"
                            onClick={() => navigate(`/admin/applicants/${apt.applicant_id}`)}
                          >
                            {applicant?.first_name} {applicant?.last_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Date & Time</p>
                          <p className="text-sm font-medium text-gray-900">
                            {formatDate(apt.appointment_date)}
                          </p>
                          <p className="text-xs text-gray-600">{apt.time_slot}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Location</p>
                          <p className="text-sm font-medium text-gray-900">{apt.center_name}</p>
                        </div>
                      </div>
                    </div>

                    {apt.notes && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700">{apt.notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      icon={<Eye className="h-3 w-3" />}
                      onClick={() => navigate(`/admin/applicants/${apt.applicant_id}`)}
                    >
                      View Applicant
                    </Button>
                    {apt.status === 'SCHEDULED' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          icon={<CheckCircle className="h-3 w-3" />}
                          onClick={() => handleMarkCompleted(apt.id)}
                          className="text-green-600 hover:bg-green-50"
                        >
                          Mark Complete
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          icon={<AlertCircle className="h-3 w-3" />}
                          onClick={() => handleMarkNoShow(apt.id)}
                          className="text-orange-600 hover:bg-orange-50"
                        >
                          No Show
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          icon={<XCircle className="h-3 w-3" />}
                          onClick={() => handleCancelAppointment(apt.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>

            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Show first page, last page, current page, and pages around current
                const showPage =
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1);

                const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
                const showEllipsisAfter = page === currentPage + 2 && currentPage < totalPages - 2;

                if (showEllipsisBefore || showEllipsisAfter) {
                  return (
                    <span key={page} className="px-2 text-gray-400">
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-ncaa-primary text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        )}
        </div>
      )}

      {/* Appointment Detail Modal */}
      {showAppointmentModal && selectedAppointment && (() => {
        const applicant = mockApplicants.find((a) => a.id === selectedAppointment.applicant_id);
        const center = mockCenters.find((c) => c.id === selectedAppointment.center_id);
        
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={() => {
              setShowAppointmentModal(false);
              setSelectedAppointment(null);
            }}
          >
            <div
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Calendar className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedAppointment.test_type}</h2>
                    <p className="text-sm text-gray-600">Ref: {selectedAppointment.booking_reference}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAppointmentModal(false);
                    setSelectedAppointment(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XCircle className="h-6 w-6 text-gray-500" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* Status Badge */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Status:</span>
                  {getStatusBadge(selectedAppointment.status)}
                </div>

                {/* Appointment Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600 flex items-center gap-2 mb-1">
                        <User className="h-4 w-4" />
                        Applicant
                      </label>
                      <p
                        className="text-base font-medium text-blue-600 cursor-pointer hover:underline"
                        onClick={() => {
                          navigate(`/admin/applicants/${selectedAppointment.applicant_id}`);
                          setShowAppointmentModal(false);
                        }}
                      >
                        {applicant?.first_name} {applicant?.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{applicant?.email}</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600 flex items-center gap-2 mb-1">
                        <Calendar className="h-4 w-4" />
                        Date
                      </label>
                      <p className="text-base font-medium text-gray-900">
                        {formatDate(selectedAppointment.appointment_date)}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600 flex items-center gap-2 mb-1">
                        <Clock className="h-4 w-4" />
                        Time
                      </label>
                      <p className="text-base font-medium text-gray-900">{selectedAppointment.time_slot}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600 flex items-center gap-2 mb-1">
                        <MapPin className="h-4 w-4" />
                        Location
                      </label>
                      <p className="text-base font-medium text-gray-900">{selectedAppointment.center_name}</p>
                      {center && (
                        <p className="text-sm text-gray-500">{center.address}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-600 mb-1 block">
                        Test Type
                      </label>
                      <p className="text-base font-medium text-gray-900">{selectedAppointment.test_type}</p>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {selectedAppointment.notes && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 mb-2 block">Notes</label>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-700">{selectedAppointment.notes}</p>
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Created</label>
                    <p className="text-sm text-gray-700">
                      {new Date(selectedAppointment.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Last Updated</label>
                    <p className="text-sm text-gray-700">
                      {new Date(selectedAppointment.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                <Button
                  variant="outline"
                  onClick={() => {
                    navigate(`/admin/applicants/${selectedAppointment.applicant_id}`);
                    setShowAppointmentModal(false);
                  }}
                  icon={<Eye className="h-4 w-4" />}
                >
                  View Applicant
                </Button>

                {selectedAppointment.status === 'SCHEDULED' && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleMarkCompleted(selectedAppointment.id)}
                      icon={<CheckCircle className="h-4 w-4" />}
                      className="text-green-600 hover:bg-green-50 border-green-300"
                    >
                      Mark Complete
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleMarkNoShow(selectedAppointment.id)}
                      icon={<AlertCircle className="h-4 w-4" />}
                      className="text-orange-600 hover:bg-orange-50 border-orange-300"
                    >
                      No Show
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleCancelAppointment(selectedAppointment.id)}
                      icon={<XCircle className="h-4 w-4" />}
                      className="text-red-600 hover:bg-red-50 border-red-300"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
